/**
 * Spectrum customizer bridge (theme ↔ Spectrum CDN)
 *
 * FLOW:
 * 1. Page load (if customizable + spectrum_sku): bridge + window.__spectrumConfig
 * 2. Customize click OR landing with ?openCustomizer=1 or ?recipeId=
 *    (cart Edit / collection / share):
 *    a) spectrumLoadProduct = __spectrumConfig.sku (backup)
 *    b) recipeId input: URL ?recipeId= (Edit/share) > metafield __spectrumConfig.recipeId
 *       (player preload) > SKU-only blank glove. Spectrum prefers URL recipeId over SKU.
 *    c) metafield recipeId is written to ?recipeId= before ESM boot so Spectrum can load it
 *    d) first time → inject ESM; later → spectrumInitializeCustomizer()
 *    e) auto-open when openCustomizer=1, or when ?recipeId= is present and
 *       openCustomizer is absent. openCustomizer=false always suppresses.
 *    f) after any open, openCustomizer=false so refresh stays on the PDP
 *       (the bridge itself writes recipeId to the URL, which would otherwise re-trigger)
 *    g) after ATC, restore the product URL (metafield sku + recipeId; drop the edited recipeId)
 * 3. Spectrum ATC → match payload SKUs to Shopify variants (PDP + upcharge catalog)
 *    → items[] SKUs plus options[].sku that match a catalog upcharge
 *    → nested cart lines: base first, then extras with parent_id + _spectrum_addon
 *    → option name/value still stored as base line properties
 *    → addon lines store _spectrum_option_name so the cart can show (+$7) on that option
 *    → if previousRecipeSetId: remove ALL cart lines with that _recipeId, then add
 *    → _preview_image on the base line (Spectrum URL first so ATC is not blocked)
 *    → POST /apps/spectrum/upload-preview in the background, then cart/change
 *      to a cdn.shopify.com URL (Plus Cart Transform). Drawer refreshes when ready.
 * Do NOT touch Kickflip.
 */
(function () {
  if (window.__spectrumBridgeLoaded) return;
  window.__spectrumBridgeLoaded = true;

  const LOG_PREFIX = '[Spectrum]';
  const SHOPIFY_CDN_PREFIX = 'https://cdn.shopify.com/';
  const PREVIEW_UPLOAD_PATH = '/apps/spectrum/upload-preview';

  /** Serializes preview uploads so ATC + cart-page backfill do not race cart/change */
  let previewUploadQueue = Promise.resolve();

  /** Dedupes upload-preview by source URL (prefetch + post-add share one POST) */
  const previewUploadBySource = new Map();

  /** Promise for the in-flight / completed ESM inject (first open) */
  let bundleLoadPromise = null;

  /** Parsed price catalog + sku/name index (static JSON on the PDP) */
  let priceCatalogCache = null;
  let priceCatalogIndex = null;

  function getConfig() {
    return window.__spectrumConfig || null;
  }

  /**
   * Docs (Load Design): ?recipeId= on the PDP loads a saved recipe.
   * Spectrum prefers recipeId over spectrumLoadProduct when both are set.
   *
   * Input priority: URL (cart Edit / share) > product metafield > none (blank SKU).
   */
  function getRecipeIdFromUrl() {
    try {
      return new URLSearchParams(window.location.search).get('recipeId') || null;
    } catch (err) {
      return null;
    }
  }

  function getRecipeIdFromConfig() {
    const recipeId = getConfig()?.recipeId;
    if (recipeId == null || recipeId === '') return null;
    return String(recipeId);
  }

  function getRecipeId() {
    return getRecipeIdFromUrl() || getRecipeIdFromConfig();
  }

  /**
   * Spectrum reads recipeId from the URL at boot. Put the resolved input there
   * without clobbering an existing Edit/share ?recipeId=.
   */
  function ensureRecipeIdInUrl(recipeId) {
    if (!recipeId || getRecipeIdFromUrl()) return;

    try {
      const url = new URL(window.location.href);
      url.searchParams.set('recipeId', recipeId);
      const search = url.searchParams.toString();
      const next = search ? `${url.pathname}?${search}${url.hash}` : `${url.pathname}${url.hash}`;
      history.replaceState(history.state, '', next);
    } catch (err) {
      logWarn('Could not write metafield recipeId to the URL:', err);
    }
  }

  function getOpenCustomizerParam() {
    try {
      return new URLSearchParams(window.location.search).get('openCustomizer');
    } catch (err) {
      return null;
    }
  }

  /**
   * openCustomizer=1 always opens. A bare ?recipeId= (share/edit link) opens
   * too, but only when openCustomizer is absent — the bridge writes both
   * recipeId and openCustomizer=false back to the URL after opening and after
   * ATC, and that false must keep a refresh on the PDP.
   */
  function shouldAutoOpenOnLoad() {
    const param = getOpenCustomizerParam();
    if (param === '1') return true;
    return param === null && Boolean(getRecipeIdFromUrl());
  }

  /**
   * Persist the auto-open flag. `false` keeps recipeId in the URL for Spectrum
   * but stops a refresh from launching the customizer again.
   */
  function setOpenCustomizerParam(value) {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('openCustomizer', String(value));
      const search = url.searchParams.toString();
      const next = search ? `${url.pathname}?${search}${url.hash}` : `${url.pathname}${url.hash}`;
      history.replaceState(history.state, '', next);
    } catch (err) {
      logWarn('Could not update openCustomizer in the URL:', err);
    }
  }

  function disableAutoOpenOnLoad() {
    if (getOpenCustomizerParam() === 'false') return;
    setOpenCustomizerParam('false');
  }

  /**
   * After ATC, drop the edited/share recipeId and return to the product’s
   * metafield inputs (sku + official recipeId). openCustomizer stays false
   * so a refresh does not relaunch the customizer.
   */
  function restoreProductUrlAfterAtc() {
    try {
      const url = new URL(window.location.href);
      const sku = getConfig()?.sku;
      const metafieldRecipeId = getRecipeIdFromConfig();

      url.searchParams.delete('recipeId');
      if (metafieldRecipeId) {
        url.searchParams.set('recipeId', metafieldRecipeId);
      }

      if (sku) {
        url.searchParams.set('sku', String(sku));
      } else {
        url.searchParams.delete('sku');
      }

      url.searchParams.set('openCustomizer', 'false');
      url.searchParams.delete('utm_source');
      url.searchParams.delete('utm_medium');
      url.searchParams.delete('utm_campaign');

      const search = url.searchParams.toString();
      const next = search ? `${url.pathname}?${search}${url.hash}` : `${url.pathname}${url.hash}`;
      history.replaceState(history.state, '', next);
      log('Restored product URL after ATC:', next);
    } catch (err) {
      logWarn('Could not restore product URL after ATC:', err);
      disableAutoOpenOnLoad();
    }
  }

  // ---------------------------------------------------------------------------
  // Helpers: logging — filter DevTools with: [Spectrum]
  // Theme setting Spectrum → "Show Spectrum debug logs" (window.__spectrumDebug).
  // Errors always print.
  // ---------------------------------------------------------------------------

  function isDebug() {
    return window.__spectrumDebug === true;
  }

  function log(...args) {
    if (!isDebug()) return;
    console.log(LOG_PREFIX, ...args);
  }

  function logWarn(...args) {
    if (!isDebug()) return;
    console.warn(LOG_PREFIX, ...args);
  }

  function logError(...args) {
    console.error(LOG_PREFIX, ...args);
  }

  function logGroup(title, fn) {
    if (!isDebug()) return;
    console.group(`${LOG_PREFIX} ${title}`);
    try {
      fn();
    } finally {
      console.groupEnd();
    }
  }

  function logPayload(payload) {
    logGroup('Add to Cart payload (from Spectrum)', () => {
      log('Full payload:', payload);
      log('recipeSetId:', payload?.recipeSetId);
      log('previousRecipeSetId:', payload?.previousRecipeSetId);
      log('items:', payload?.items);
      log('options:', payload?.options);
      log('primaryThumbnailAngle:', payload?.primaryThumbnailAngle);
      log('thumbnails:', payload?.thumbnails);
      log('thumbnailsByAngle:', payload?.thumbnailsByAngle);
    });
    window.__spectrumLastCartPayload = payload;
  }

  // ---------------------------------------------------------------------------
  // Helpers: resolve Shopify variant + thumbnail from payload
  // ---------------------------------------------------------------------------

  function getThumbnailUrl(payload) {
    const list = payload?.thumbnails || payload?.thumbnailsByAngle || [];
    const preferred = payload?.primaryThumbnailAngle;
    if (preferred && list.length) {
      const match = list.find((t) => t.angleName === preferred);
      if (match?.url) return match.url;
    }
    return list[0]?.url || null;
  }

  const RESERVED_PROPERTY_KEYS = new Set([
    '_recipeId',
    '_preview_image',
    '_spectrum_addon',
    '_spectrum_addon_sku',
    '_spectrum_option_name',
    'spectrumThumbnail',
    '_spectrumThumbnail',
    'Recipe ID',
    'Share URL'
  ]);

  /**
   * Public share link for the saved design (order + cart).
   * Kept free of tracking params — the cart displays this verbatim.
   */
  function buildShareUrl(recipeSetId, sku) {
    const url = new URL(window.location.pathname, window.location.origin);
    url.searchParams.set('recipeId', String(recipeSetId));
    url.searchParams.set('openCustomizer', '1');
    if (sku) url.searchParams.set('sku', String(sku));
    return url.toString();
  }

  function getCartUi() {
    return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  }

  function isShopifyCdnUrl(url) {
    return typeof url === 'string' && url.indexOf(SHOPIFY_CDN_PREFIX) === 0;
  }

  function isSpectrumAddonLine(item) {
    return item?.properties?._spectrum_addon === 'true';
  }

  function getPreviewSourceUrl(item) {
    const props = item?.properties || {};
    return props._preview_image || props.spectrumThumbnail || props._spectrumThumbnail || null;
  }

  function enqueuePreviewWork(work) {
    previewUploadQueue = previewUploadQueue.catch(() => {}).then(work);
    return previewUploadQueue;
  }

  function findBaseCartLine(items, recipeSetId) {
    const recipeId = recipeSetId != null ? String(recipeSetId) : null;
    return (items || []).find((item) => {
      if (!item || isSpectrumAddonLine(item)) return false;
      if (recipeId && item.properties?._recipeId !== recipeId) return false;
      return Boolean(getPreviewSourceUrl(item) || item.properties?._recipeId);
    }) || null;
  }

  function uploadPreviewToShopify(imageUrl) {
    const cached = previewUploadBySource.get(imageUrl);
    if (cached) {
      log('Reusing upload-preview for', imageUrl);
      return cached;
    }

    const upload = (async () => {
      log('POST', PREVIEW_UPLOAD_PATH, imageUrl);

      const response = await fetch(PREVIEW_UPLOAD_PATH, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ imageUrl })
      });

      let payload = {};
      try {
        payload = await response.json();
      } catch (err) {
        payload = {};
      }

      if (!response.ok || !payload?.shopifyUrl) {
        throw new Error(
          payload.message ||
            payload.error ||
            `upload-preview failed (${response.status})`
        );
      }

      log('upload-preview shopifyUrl:', payload.shopifyUrl);
      return payload.shopifyUrl;
    })();

    previewUploadBySource.set(imageUrl, upload);
    upload.catch(() => {
      previewUploadBySource.delete(imageUrl);
    });
    return upload;
  }

  function prefetchPreviewUpload(imageUrl) {
    if (!imageUrl || isShopifyCdnUrl(imageUrl)) return;
    uploadPreviewToShopify(imageUrl).catch((err) => {
      logError('Preview prefetch failed', err);
    });
  }

  function refreshPreviewInBackground(addData, recipeSetId) {
    ensurePreviewAfterAdd(addData, recipeSetId)
      .then((previewData) => {
        if (!previewData) return;
        const cartUi = getCartUi();
        if (!cartUi) return;
        cartUi.renderContents(previewData);
        syncDrawerHiddenProperties();
        log('Cart drawer refreshed after preview upload');
      })
      .catch((err) => {
        logError(
          'Preview upload failed — cart embed can still use the Spectrum URL; checkout needs cdn.shopify.com',
          err
        );
      });
  }

  async function changeLinePreviewImage(item, shopifyUrl) {
    const properties = Object.assign({}, item.properties || {}, {
      _preview_image: shopifyUrl
    });
    const cart = getCartUi();
    const body = {
      id: item.key,
      quantity: item.quantity,
      properties
    };

    if (cart && typeof cart.getSectionsToRender === 'function') {
      body.sections = cart.getSectionsToRender().map((section) => section.section);
      body.sections_url = window.location.pathname;
    }

    logGroup('cart/change.js preview image', () => {
      log('line key:', item.key);
      log('_preview_image:', shopifyUrl);
    });

    const response = await fetch(`${routes.cart_change_url}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify(body)
    });
    const data = await response.json();

    if (data.status) {
      throw new Error(data.description || data.message || 'Cart change failed');
    }

    return data;
  }

  /**
   * Spectrum contract: checkout Plus only swaps the image when _preview_image
   * is a Shopify CDN URL. Cart embed can use any https:// URL.
   */
  async function ensureShopifyPreviewImage(item) {
    if (!item?.key || isSpectrumAddonLine(item)) return null;

    const current = item.properties?._preview_image;
    if (isShopifyCdnUrl(current)) {
      log('Preview already on Shopify CDN:', item.key);
      return null;
    }

    const sourceUrl = getPreviewSourceUrl(item);
    if (!sourceUrl) return null;

    if (isShopifyCdnUrl(sourceUrl)) {
      return changeLinePreviewImage(item, sourceUrl);
    }

    const shopifyUrl = await uploadPreviewToShopify(sourceUrl);
    return changeLinePreviewImage(item, shopifyUrl);
  }

  async function processPreviewImageUploads() {
    if (!window.routes?.cart_url) return null;

    return enqueuePreviewWork(async () => {
      const cartRes = await fetch(`${routes.cart_url}.js`);
      const cartData = await cartRes.json();
      let lastChange = null;

      for (const item of cartData.items || []) {
        try {
          const changed = await ensureShopifyPreviewImage(item);
          if (changed) lastChange = changed;
        } catch (err) {
          logError('Preview upload skipped for line', item.key, err);
        }
      }

      return lastChange;
    });
  }

  async function ensurePreviewAfterAdd(addData, recipeSetId) {
    return enqueuePreviewWork(async () => {
      let baseLine = findBaseCartLine(addData?.items, recipeSetId);

      if (!baseLine?.key) {
        const cartRes = await fetch(`${routes.cart_url}.js`);
        const cartData = await cartRes.json();
        baseLine = findBaseCartLine(cartData.items, recipeSetId);
      }

      if (!baseLine) {
        logWarn('No base line found for preview upload');
        return null;
      }

      return ensureShopifyPreviewImage(baseLine);
    });
  }

  function getPriceCatalog() {
    if (priceCatalogCache) return priceCatalogCache;

    const el = document.querySelector('[data-spectrum-price-catalog]');
    if (!el) {
      priceCatalogCache = [];
      priceCatalogIndex = new Map();
      return priceCatalogCache;
    }

    try {
      const parsed = JSON.parse(el.textContent);
      priceCatalogCache = Array.isArray(parsed) ? parsed : [];
    } catch (err) {
      logError('Failed to parse price catalog', err);
      priceCatalogCache = [];
    }

    const index = new Map();
    const catalog = priceCatalogCache;
    for (let i = 0; i < catalog.length; i++) {
      const row = catalog[i];
      if (row.sku != null) {
        const skuKey = String(row.sku);
        if (!index.has(skuKey)) index.set(skuKey, row);
      }
      if (row.name != null) {
        const nameKey = String(row.name);
        if (!index.has(nameKey)) index.set(nameKey, row);
      }
    }
    priceCatalogIndex = index;
    return priceCatalogCache;
  }

  function getCatalogIndex() {
    if (!priceCatalogIndex) getPriceCatalog();
    return priceCatalogIndex;
  }

  function findCatalogRow(skuOrName) {
    if (!skuOrName) return null;
    return getCatalogIndex().get(String(skuOrName)) || null;
  }

  function isUpchargeRow(row) {
    return Boolean(row && row.type === 'upcharge');
  }

  function spectrumItemToAvailabilityRow(row) {
    if (!row) return null;
    return {
      sku: row.sku,
      name: row.name,
      type: row.type,
      price: Number(row.price),
      listPrice: row.listPrice == null ? null : Number(row.listPrice),
      isoCurrency: row.isoCurrency,
      inStock: Boolean(row.inStock),
      options: row.options || null
    };
  }

  function registerPriceCallbacks() {
    window.spectrumItemPricesAndAvailabilityCallback = function (itemsBySkuOrName) {
      const catalog = getPriceCatalog();
      const keys = Array.isArray(itemsBySkuOrName) ? itemsBySkuOrName : [];
      const missing = [];
      let rows;

      if (!keys.length) {
        rows = catalog.map(spectrumItemToAvailabilityRow).filter(Boolean);
      } else {
        rows = keys
          .map((key) => {
            const row = spectrumItemToAvailabilityRow(findCatalogRow(key));
            if (!row) missing.push(key);
            return row;
          })
          .filter(Boolean);
      }

      logGroup('Price & availability callback', () => {
        log('Requested SKUs/names:', keys.length ? keys : '(none — returning full catalog)');
        log('Returned rows:', rows);
        if (missing.length) logWarn('No catalog match for:', missing);
      });

      return Promise.resolve(rows);
    };

    window.spectrumTotalPriceCallback = function (items) {
      const catalog = getPriceCatalog();
      const isoCurrency = catalog[0]?.isoCurrency || 'USD';
      let price = 0;
      const breakdown = [];

      (items || []).forEach((item) => {
        const row = findCatalogRow(item?.sku) || findCatalogRow(item?.name);
        const qty = Number(item?.quantity) || 1;
        const unit = row ? Number(row.price) : 0;
        if (row) price += unit * qty;
        breakdown.push({
          sku: item?.sku,
          name: item?.name,
          qty,
          unitPrice: row ? unit : null,
          matched: Boolean(row)
        });
      });

      const result = { price, isoCurrency, options: null };
      logGroup('Total price callback', () => {
        log('Items from Spectrum:', items);
        log('Breakdown:', breakdown);
        log('Result:', result);
      });
      return Promise.resolve(result);
    };

    log('Price callbacks registered on window');
  }

  /**
   * Spectrum sends options as [{ name, value }, ...]. Also accept a plain object.
   * Returns { Size: '11', 'Wrist Type': 'Traditional', ... } for line-item properties.
   */
  function normalizeOptions(options) {
    const mapped = {};

    if (Array.isArray(options)) {
      options.forEach((option) => {
        const key = option?.name != null ? String(option.name).trim() : '';
        const value = option?.value;
        if (!key || RESERVED_PROPERTY_KEYS.has(key) || value == null || value === '') return;
        mapped[key] = String(value);
      });
      return mapped;
    }

    if (options && typeof options === 'object') {
      Object.entries(options).forEach(([key, value]) => {
        const name = String(key).trim();
        if (!name || RESERVED_PROPERTY_KEYS.has(name) || value == null || value === '') return;
        mapped[name] = String(value);
      });
    }

    return mapped;
  }

  function resolveAddonOptionName(addon, options) {
    if (addon?.optionName) return String(addon.optionName).trim();

    const sku = addon?.sku;
    if (sku && Array.isArray(options)) {
      const match = options.find((option) => option && String(option.sku) === String(sku));
      if (match?.name != null && String(match.name).trim()) return String(match.name).trim();
    }

    if (addon?.name != null && String(addon.name).trim()) return String(addon.name).trim();
    return '';
  }

  /**
   * Overlay "Hide line item properties" lives on the drawer wrapper, but /cart/add
   * section HTML uses schema defaults (no See details). CSS hides .cart-item__properties.
   * After a drawer body swap, inject See details if missing.
   */
  function getHidePropertiesDrawer() {
    return document.querySelector('.cart-drawer[data-hide-cart-item-properties], .cart-drawer--hide-item-properties');
  }

  function syncDrawerHiddenProperties() {
    const root = getHidePropertiesDrawer();
    if (!root) return;

    const label = (root.dataset.seeDetailsLabel || '').trim() || 'See details';

    const cartUrl = window.routes?.cart_url || '/cart';

    root.querySelectorAll('.cart-item').forEach((item) => {
      if (item.querySelector('.cart-item__property-button')) return;

      const wrap = document.createElement('div');
      wrap.className = 'cart-item__property-button';
      wrap.dataset.spectrumInjected = 'true';
      const link = document.createElement('a');
      link.href = cartUrl;
      link.className = 'button button--text button--small';
      link.textContent = label;
      wrap.appendChild(link);

      const edit = item.querySelector('.cart-item__spectrum-edit');
      const bottom = item.querySelector('.cart-item__bottom-content');
      if (edit) {
        edit.insertAdjacentElement('afterend', wrap);
      } else if (bottom) {
        bottom.insertAdjacentElement('beforebegin', wrap);
      } else {
        item.querySelector('.cart-item__content')?.appendChild(wrap);
      }
    });
  }

  function observeDrawerPropertySync() {
    if (!getHidePropertiesDrawer()) return;

    const body = document.getElementById('CartDrawer-Body');
    if (!body || body.dataset.spectrumPropertySync === 'true') return;
    body.dataset.spectrumPropertySync = 'true';

    const observer = new MutationObserver(() => {
      syncDrawerHiddenProperties();
    });
    observer.observe(body, { childList: true, subtree: true });
    syncDrawerHiddenProperties();
  }

  /**
   * Match Shopify variant by SKU only. No PDP-selected fallback.
   * Reads Spectrum boot JSON first so ATC works without a variant picker
   * (products that only have the default variant).
   */
  function resolveVariantId(payloadSku) {
    log('Resolving Shopify variant for Spectrum SKU (no fallback):', payloadSku);

    if (!payloadSku) {
      logError('Payload SKU is empty — aborting (no fallback)');
      return null;
    }

    const catalogRow = findCatalogRow(payloadSku);
    if (catalogRow?.variantId) {
      log('Matched variant via price catalog:', catalogRow.variantId, catalogRow);
      return catalogRow.variantId;
    }

    const variantsEl =
      document.querySelector('[data-spectrum-variants]') ||
      document.querySelector('[data-all-variants]');

    if (variantsEl) {
      try {
        const variants = JSON.parse(variantsEl.textContent);
        const match = variants.find((v) => v.sku && v.sku === payloadSku);
        if (match?.id) {
          log('Matched variant by SKU:', match.id, match);
          return match.id;
        }
        log(
          'No variant.sku match. Configure the Shopify variant SKU to equal Spectrum SKU.',
          'Looking for:',
          payloadSku,
          '| Variant SKUs:',
          variants.map((v) => v.sku || '(empty)')
        );
      } catch (err) {
        logError('Failed to parse data-all-variants', err);
      }
    } else {
      log('No [data-all-variants] on PDP — trying __spectrumConfig.variantSku');
    }

    const config = getConfig();
    if (config?.variantId && config.variantSku && config.variantSku === payloadSku) {
      log('Matched variant via __spectrumConfig.variantSku:', config.variantId);
      return config.variantId;
    }

    return null;
  }

  /**
   * Edit flow: Shopify cannot update line-item properties in place.
   * Remove every cart line whose _recipeId matches (base + nested addons).
   */
  async function removeCartLinesByRecipeId(previousRecipeSetId) {
    if (!previousRecipeSetId) return false;

    log('Edit ATC — looking for cart lines with _recipeId:', previousRecipeSetId);

    const cartRes = await fetch(`${routes.cart_url}.js`);
    const cartData = await cartRes.json();
    const matching = (cartData.items || []).filter(
      (item) => item.properties && item.properties._recipeId === previousRecipeSetId
    );

    if (!matching.length) {
      log('No cart lines found for previousRecipeSetId — will add as new item:', previousRecipeSetId);
      return false;
    }

    logGroup('Edit ATC — remove previous recipe', () => {
      log('_recipeId:', previousRecipeSetId);
      log(
        'Matching lines:',
        matching.map((item) => ({
          key: item.key,
          title: item.title,
          _recipeId: item.properties._recipeId,
          _spectrum_addon: item.properties._spectrum_addon,
          _spectrum_addon_sku: item.properties._spectrum_addon_sku
        }))
      );
    });

    const updates = {};
    matching.forEach((item) => {
      updates[item.key] = 0;
    });

    const cart = getCartUi();
    const changeBody = { updates };

    if (cart && typeof cart.getSectionsToRender === 'function') {
      changeBody.sections = cart.getSectionsToRender().map((section) => section.section);
      changeBody.sections_url = window.location.pathname;
    }

    const changeRes = await fetch(`${routes.cart_update_url}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify(changeBody)
    });
    const changeData = await changeRes.json();

    if (changeData.status) {
      logError('Failed to remove previous recipe lines:', changeData);
      throw new Error(changeData.description || changeData.message || 'Cart update failed');
    }

    if (cart && changeData.sections) {
      cart.renderContents(changeData, false);
      syncDrawerHiddenProperties();
    }

    log('Previous custom build removed from cart');
    return true;
  }

  /**
   * One pass: items[] → base vs upcharge, then options[].sku → extra upcharges.
   * Catalog + collected addons are Maps so SKU lookup/dedupe is O(1).
   */
  function splitPayloadItems(payload) {
    const catalogByKey = getCatalogIndex();
    const addonsBySku = new Map();
    const items = Array.isArray(payload?.items) ? payload.items : [];
    const unmatchedOptionSkus = [];
    let base = null;

    function addUpcharge(sku, addon) {
      if (!sku) return;
      const key = String(sku);
      const existing = addonsBySku.get(key);
      if (existing) {
        if (!existing.optionName && addon?.optionName) existing.optionName = addon.optionName;
        if (!existing.name && addon?.name) existing.name = addon.name;
        return;
      }
      addonsBySku.set(key, addon);
    }

    function addOptionUpcharge(option) {
      const sku = option && option.sku;
      if (!sku) return;

      const key = String(sku);
      const row = catalogByKey.get(key);
      if (!isUpchargeRow(row)) {
        unmatchedOptionSkus.push(key);
        return;
      }

      addUpcharge(String(row.sku), {
        sku: String(row.sku),
        name: option.name != null ? String(option.name) : row.name,
        optionName: option.name != null ? String(option.name) : '',
        quantity: 1
      });
    }

    function scanOptions(options) {
      if (!Array.isArray(options)) return;
      for (let i = 0; i < options.length; i++) addOptionUpcharge(options[i]);
    }

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const row = item && item.sku ? catalogByKey.get(String(item.sku)) : null;
      if (isUpchargeRow(row)) {
        addUpcharge(String(row.sku), item);
      } else if (!base) {
        base = item;
      }
      scanOptions(item && item.options);
    }

    scanOptions(payload && payload.options);

    if (!base) {
      const config = getConfig();
      base = { sku: config?.variantSku || config?.sku, quantity: 1 };
    }

    const addons = [...addonsBySku.values()];

    logGroup('ATC split payload.items + options[].sku', () => {
      log('Incoming items:', items);
      log('Base:', base);
      log('Upcharges:', addons);
      if (unmatchedOptionSkus.length) {
        logWarn('Option SKUs with no catalog upcharge match:', unmatchedOptionSkus);
      }
      if (!addons.length) {
        logWarn('No upcharges in payload — checkout may require at least one _spectrum_addon line');
      }
    });

    return { base, addons };
  }

  async function addToShopifyCart(props) {
    const { base, addons, recipeSetId, thumbnailUrl, options } = props;
    log('addToShopifyCart called with props:', props);
    const cart = getCartUi();
    const baseVariantId = resolveVariantId(base?.sku);
    if (!baseVariantId) {
      throw new Error('No Shopify variant SKU match for Spectrum base item');
    }

    const quantity = base?.quantity || 1;
    const optionProperties = normalizeOptions(options);
    const baseProperties = Object.assign({ _recipeId: String(recipeSetId) }, optionProperties);
    baseProperties['Recipe ID'] = String(recipeSetId);
    baseProperties['Share URL'] = buildShareUrl(recipeSetId, base?.sku);
    if (thumbnailUrl) {
      baseProperties._preview_image = thumbnailUrl;
    }

    const cartItems = [
      {
        id: baseVariantId,
        quantity,
        properties: baseProperties
      }
    ];

    addons.forEach((addon) => {
      const addonVariantId = resolveVariantId(addon.sku);
      if (!addonVariantId) {
        logError('Skipping upcharge — no Shopify variant for SKU:', addon.sku);
        return;
      }
      const addonProperties = {
        _recipeId: String(recipeSetId),
        _spectrum_addon: 'true',
        _spectrum_addon_sku: String(addon.sku)
      };
      const optionName = resolveAddonOptionName(addon, options);
      if (optionName) addonProperties._spectrum_option_name = optionName;

      cartItems.push({
        id: addonVariantId,
        quantity: addon.quantity || quantity,
        parent_id: baseVariantId,
        properties: addonProperties
      });
    });

    if (addons.length === 0) {
      logWarn(
        'No upcharge lines in this ATC. Checkout validation requires at least one _spectrum_addon line per _recipeId.'
      );
    }

    const body = { items: cartItems };

    if (cart && typeof cart.getSectionsToRender === 'function') {
      body.sections = cart.getSectionsToRender().map((section) => section.section);
      body.sections_url = window.location.pathname;
      if (typeof cart.setActiveElement === 'function') {
        cart.setActiveElement(document.activeElement);
      }
    }

    const config = fetchConfig('json');
    config.body = JSON.stringify(body);

    logGroup('Shopify cart/add.js request', () => {
      log('Full body (minus sections):', { items: cartItems });
      log('Base properties:', baseProperties);
      log(
        'Addon lines:',
        cartItems.slice(1).map((item) => ({
          id: item.id,
          parent_id: item.parent_id,
          properties: item.properties
        }))
      );
      log('cart UI:', cart ? cart.tagName : 'none (will redirect to /cart)');
    });

    prefetchPreviewUpload(thumbnailUrl);

    const response = await fetch(`${routes.cart_add_url}`, config);
    const data = await response.json();

    logGroup('Shopify cart/add.js response', () => {
      log(data);
      if (data.items) {
        log(
          'Returned lines:',
          data.items.map((item) => ({
            key: item.key,
            title: item.title,
            variant_id: item.variant_id,
            properties: item.properties,
            parent_relationship: item.parent_relationship
          }))
        );
      }
    });

    if (data.status) {
      throw new Error(data.description || data.message || 'Cart add failed');
    }

    if (cart) {
      cart.renderContents(data);
      syncDrawerHiddenProperties();
      log('Cart drawer/notification updated');
      if (thumbnailUrl) {
        refreshPreviewInBackground(data, recipeSetId);
      }
    } else {
      log('No cart drawer/notification — redirecting to cart page');
      window.location = window.routes.cart_url;
    }

    return data;
  }

  // ---------------------------------------------------------------------------
  // Spectrum callback: Add to Cart
  // ---------------------------------------------------------------------------

  window.spectrumAddToCart = async function spectrumAddToCart(payload) {
    log('spectrumAddToCart called by Spectrum');
    logPayload(payload);

    const recipeSetId = payload?.recipeSetId;
    const previousRecipeSetId = payload?.previousRecipeSetId || null;
    const thumbnailUrl = getThumbnailUrl(payload);
    const { base, addons } = splitPayloadItems(payload);
    const options = payload?.options || base?.options || null;

    log('ATC split — base:', base, '| addons:', addons);
    log('Thumbnail from payload:', thumbnailUrl);

    if (!recipeSetId) {
      logError('Missing recipeSetId — aborting cart add');
      throw new Error('Spectrum payload missing recipeSetId');
    }

    if (!base?.sku) {
      logError('ATC aborted: no base SKU in payload or config');
      throw new Error('Spectrum payload missing base SKU');
    }

    if (!resolveVariantId(base.sku)) {
      logError(
        'ATC aborted: no Shopify variant with matching SKU. Set variant SKU in Admin to:',
        base.sku
      );
      throw new Error('No Shopify variant SKU match for Spectrum ATC');
    }

    try {
      const result = await addToShopifyCart({
        base,
        addons,
        recipeSetId,
        thumbnailUrl,
        options
      });

      log('Add result before remove check:', result);

      if (previousRecipeSetId && result && !result.status) {
        await removeCartLinesByRecipeId(previousRecipeSetId);
      } else if (previousRecipeSetId) {
        logError('Skipping remove — add did not succeed:', result);
      }

      log('ATC handoff complete', previousRecipeSetId ? '(replaced previous recipe)' : '(new item)');
      restoreProductUrlAfterAtc();
      return { ok: true, result, replacedPrevious: Boolean(previousRecipeSetId && result && !result.status) };
    } catch (error) {
      logError('ATC handoff failed:', error);
      throw error;
    }
  };

  // ---------------------------------------------------------------------------
  // Lazy-load Spectrum ESM
  // ---------------------------------------------------------------------------

  function loadSpectrumBundle() {
    if (bundleLoadPromise) {
      log('ESM already loading/loaded — reusing promise');
      return bundleLoadPromise;
    }

    const src = getConfig()?.customizerSrc;
    if (!src) {
      logError('Missing __spectrumConfig.customizerSrc');
      return Promise.reject(new Error('Missing __spectrumConfig.customizerSrc'));
    }

    if (!document.getElementById('spectrum-customizer')) {
      logError('Missing #spectrum-customizer mount div');
      return Promise.reject(new Error('Missing #spectrum-customizer mount div'));
    }

    bundleLoadPromise = new Promise((resolve, reject) => {
      log('Injecting Spectrum ESM (first open):', src);

      const script = document.createElement('script');
      script.type = 'module';
      script.src = src;
      script.dataset.spectrumBundle = 'true';

      script.onerror = () => {
        bundleLoadPromise = null;
        logError('Failed to load Spectrum customizer.esm.js:', src);
        reject(new Error('Failed to load Spectrum customizer.esm.js'));
      };

      const startedAt = Date.now();
      const maxWaitMs = 30000;

      const poll = () => {
        if (typeof window.spectrumInitializeCustomizer === 'function') {
          log('Spectrum ESM ready — spectrumInitializeCustomizer is available');
          resolve();
          return;
        }
        if (Date.now() - startedAt > maxWaitMs) {
          bundleLoadPromise = null;
          logError('Timed out waiting for spectrumInitializeCustomizer');
          reject(new Error('Timed out waiting for spectrumInitializeCustomizer'));
          return;
        }
        requestAnimationFrame(poll);
      };

      script.onload = () => {
        log('Spectrum ESM script element loaded — waiting for initialize API…');
        poll();
      };

      document.head.appendChild(script);
      poll();
    });

    return bundleLoadPromise;
  }

  // ---------------------------------------------------------------------------
  // Open customizer (Customize click OR Edit via ?recipeId=)
  // ---------------------------------------------------------------------------

  async function openCustomizer(triggerEl) {
    const sku = getConfig()?.sku;
    const recipeId = getRecipeId();

    // Docs: at least one of recipeId (URL or metafield) or spectrumLoadProduct (SKU) is required
    if (!recipeId && !sku) {
      logError('Cannot open: need __spectrumConfig.sku and/or a recipeId (URL or metafield)');
      return;
    }

    // Spectrum Load Design reads ?recipeId=; persist metafield input before ESM boot
    ensureRecipeIdInUrl(recipeId);

    // A recipeId in the URL now auto-opens on load, so once the customizer is
    // opening, pin openCustomizer=false — otherwise a refresh would relaunch it.
    if (getRecipeIdFromUrl()) {
      disableAutoOpenOnLoad();
    }

    // Always set SKU when available; Spectrum ignores it if recipeId is in the URL
    if (sku) {
      window.spectrumLoadProduct = sku;
    }

    logGroup('Opening customizer', () => {
      log('spectrumLoadProduct (SKU):', window.spectrumLoadProduct || '(none)');
      log('recipeId (URL > metafield):', recipeId || '(none)');
      log('URL recipeId:', getRecipeIdFromUrl() || '(none)');
      log('metafield recipeId:', getRecipeIdFromConfig() || '(none)');
      log('__spectrumConfig:', getConfig());
    });

    const alreadyReady = typeof window.spectrumInitializeCustomizer === 'function';

    try {
      if (!alreadyReady) {
        log('First open — loading Spectrum ESM (will auto-launch)…');
        triggerEl?.setAttribute('aria-busy', 'true');
        await loadSpectrumBundle();
        triggerEl?.removeAttribute('aria-busy');
        log('First open complete (auto-launch handled by Spectrum)');
        return;
      }

      log('Relaunching via spectrumInitializeCustomizer()…');
      await window.spectrumInitializeCustomizer();
      log('Customizer relaunched');
    } catch (error) {
      triggerEl?.removeAttribute('aria-busy');
      logError('Failed to open customizer:', error);
    }
  }

  async function launchCustomizer(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    await openCustomizer(event?.currentTarget);
  }

  function bindCustomizeButtons() {
    const buttons = document.querySelectorAll('[data-spectrum-launch]');
    log('Binding Customize buttons:', buttons.length);

    buttons.forEach((button) => {
      if (button.dataset.spectrumBound === 'true') return;
      button.dataset.spectrumBound = 'true';
      button.addEventListener('click', launchCustomizer);
    });
  }

  /**
   * Auto-open when ?openCustomizer=1 (cart Edit, collection, share) or when
   * ?recipeId= is present without an openCustomizer param (bare share link).
   * Flip the flag to false first so a refresh cannot relaunch.
   */
  function maybeAutoOpenOnLoad() {
    if (!shouldAutoOpenOnLoad()) return;

    log(
      getOpenCustomizerParam() === '1'
        ? 'Detected ?openCustomizer=1 — auto-opening customizer'
        : 'Detected ?recipeId= — auto-opening customizer'
    );
    disableAutoOpenOnLoad();
    openCustomizer(null);
  }

  // ---------------------------------------------------------------------------
  // Init
  // ---------------------------------------------------------------------------

  function init() {
    registerPriceCallbacks();
    bindCustomizeButtons();
    observeDrawerPropertySync();
    maybeAutoOpenOnLoad();
    processPreviewImageUploads();

    const catalog = getPriceCatalog();
    logGroup('Bridge ready', () => {
      log('__spectrumConfig:', getConfig());
      log('URL recipeId:', getRecipeIdFromUrl() || '(none)');
      log('metafield recipeId:', getRecipeIdFromConfig() || '(none)');
      log('Price catalog rows:', catalog.length);
      log('Price catalog:', catalog);
      log('Last ATC payload: window.__spectrumLastCartPayload (after add)');
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
